import React, { useState, useEffect } from 'react';
import { FormSubmission } from '../types';
import { adminFormsService } from '../services/admin-forms.service';
import { COLORS } from '../../../../core/constants/colors';

interface SubmissionListProps {
  onViewSubmission: (submission: FormSubmission) => void;
  searchQuery?: string;
  filterCompanyId?: number | null;
  onRefresh?: () => void;
  companies?: Array<{ id: number; name: string }>;
}

export const SubmissionList: React.FC<SubmissionListProps> = ({ 
  onViewSubmission, 
  searchQuery: externalSearchQuery = '',
  filterCompanyId: externalFilterCompanyId = null,
  onRefresh,
  companies = [],
}) => {
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Load submissions on mount and when filter changes
  useEffect(() => {
    loadSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFilterCompanyId]);

  const loadSubmissions = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params: any = {};
      if (externalFilterCompanyId) {
        params.company_id = externalFilterCompanyId;
      }
      const data = await adminFormsService.getAllSubmissions(params);
      setSubmissions(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSubmissions = submissions.filter((submission) => {
    const matchesSearch = externalSearchQuery === '' || 
      submission.id.toString().includes(externalSearchQuery) ||
      submission.submitted_by?.toLowerCase().includes(externalSearchQuery.toLowerCase());
    return matchesSearch;
  });

  const paginatedSubmissions = filteredSubmissions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [externalSearchQuery, externalFilterCompanyId]);

  const getCompanyName = (companyId: number) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || `Company ${companyId}`;
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent mb-2"
          style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
        />
        <p className="text-gray-600">Loading submissions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg" style={{ backgroundColor: '#FFEBEE' }}>
        <p className="text-sm" style={{ color: '#C62828' }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Submissions List */}
      {paginatedSubmissions.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <svg className="w-12 h-12 mx-auto mb-3" style={{ color: '#D1D5DB' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>
            {externalSearchQuery || externalFilterCompanyId ? 'No submissions found matching your filters' : 'No submissions found'}
          </p>
        </div>
      ) : (
        <>
        <div className="space-y-3">
            {paginatedSubmissions.map((submission) => (
            <div
              key={submission.id}
              className="border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer"
              style={{ borderColor: '#E5E7EB' }}
              onClick={() => onViewSubmission(submission)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-base" style={{ color: '#1F2937' }}>
                      Submission #{submission.id}
                    </h3>
                    {submission.completeness_score !== undefined && (
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{
                          backgroundColor: submission.completeness_score >= 80 ? '#E8F5E9' : submission.completeness_score >= 50 ? '#FFF3E0' : '#FFEBEE',
                          color: submission.completeness_score >= 80 ? '#2E7D32' : submission.completeness_score >= 50 ? '#E65100' : '#C62828',
                        }}
                      >
                        {submission.completeness_score}% Complete
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                    <span>Schema ID: {submission.form_schema_id}</span>
                    <span>Company: {getCompanyName(submission.company_id)}</span>
                    {submission.submitted_by && (
                      <span>By: {submission.submitted_by}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400">
                    Submitted: {new Date(submission.submitted_at).toLocaleString()}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewSubmission(submission);
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-all hover:shadow-sm"
                  style={{ backgroundColor: COLORS.admin.primary }}
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 pt-4 border-t flex flex-wrap items-center justify-between gap-4" style={{ borderColor: '#E5E7EB' }}>
              <div className="text-sm" style={{ color: '#6B7280' }}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredSubmissions.length)} of {filteredSubmissions.length} submissions
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm font-medium" style={{ color: '#6B7280' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

